import { useEffect } from 'react';
import { googleFamiliesFor, FONTS } from '../../../utils/portalLayout';

/**
 * The motion a Portal card is allowed — sanctioned exceptions to the design system's 200ms rule
 * (handoff §10). Namespaced `prc` because index.css already has a `prDrift` that means something
 * else. Injected once into <head>; every card shares it.
 *
 * Under reduced motion every animation stops on its first frame. A card that is off-screen (or
 * told it is paused) freezes its animations instead of burning the GPU behind a closed viewer.
 */
export const PORTAL_CANVAS_CSS = `
@keyframes prcRingSlide { from { background-position: 0 0 } to { background-position: 200% 0 } }
@keyframes prcSpin { to { transform: rotate(360deg) } }
@keyframes prcSweep { from { transform: rotate(calc(var(--r) - 7deg)) } to { transform: rotate(calc(var(--r) + 7deg)) } }
@keyframes prcGlow { from { opacity: .6 } to { opacity: 1 } }
@keyframes prcHue { 0%, 100% { background-position: 0% 50% } 50% { background-position: 100% 50% } }
@keyframes prcTwinkle { 0%, 100% { opacity: 0; transform: scale(.5) } 50% { opacity: 1; transform: scale(1) } }
@keyframes prcFleck { 0% { opacity: 0; transform: translateX(0) } 15% { opacity: .9 } 75% { opacity: .9 } 100% { opacity: 0; transform: translateX(-160px) } }
@keyframes prcDrift { 0%, 100% { transform: translate(0, 0) } 50% { transform: translate(var(--dx), var(--dy)) } }
@keyframes prcGridMove { from { background-position: 0 0, 0 0 } to { background-position: 0 44px, 0 0 } }
@keyframes prcBand { from { transform: translateX(-35%) } to { transform: translateX(35%) } }
@keyframes prcJitter { 0%, 90%, 100% { transform: none } 92% { transform: translateX(6px) } 94% { transform: translateX(-5px) } 96% { transform: translateX(2px) } }
@keyframes prcRipple { from { opacity: .9; transform: scale(1) } to { opacity: 0; transform: scale(2.1) } }
@keyframes prcPop { from { opacity: 0; transform: scale(.4) } to { opacity: 1; transform: scale(1) } }
@keyframes prcRise { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
@keyframes prcFadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes prcDriftZ { from { transform: scale(1.08) } to { transform: scale(1.18) } }
@keyframes prcSlideUp { from { transform: translateY(100%) } to { transform: none } }
@keyframes prcNudge { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-4px) } }
@keyframes prcTipIn { from { opacity: 0; transform: translateX(var(--dx, 0px)) translateY(6px) scale(.96) } to { opacity: 1; transform: translateX(var(--dx, 0px)) } }
.prc-card[data-paused="true"] *, .prc-card[data-paused="true"] { animation-play-state: paused !important; }
/* The picture is never a thing you interact with: a hold must not open the browser's save sheet. */
.prc-card img, .prc-card video { -webkit-touch-callout: none; -webkit-user-select: none; user-select: none; -webkit-user-drag: none; pointer-events: none; }
.prc-ctl { font-family: inherit; cursor: pointer; text-transform: uppercase; transition: background-color .2s, color .2s; }
.prc-ctl:hover { background: #fff !important; color: #000 !important; }
.prc-pill { font-family: inherit; cursor: pointer; border: 0; transition: background-color .2s, color .2s, opacity .2s; }
.prc-pill:hover { background: #fff !important; color: #000 !important; }
.prc-row { scrollbar-width: none; }
.prc-row::-webkit-scrollbar { display: none; }
.prc-words[contenteditable] { caret-color: #00FFFF; outline: none; -webkit-user-select: text; user-select: text; }
/* The text style bar sits on the card's bottom edge, which on a phone is the bottom of the
   screen — under the keyboard that the same tap raised. It rides up on an inline bottom
   (useKeyboardInset measures how much of the card the keyboard is over); this is only the
   easing, so it travels with the keyboard instead of teleporting. */
.prc-style-bar { transition: bottom 180ms cubic-bezier(.2,.7,.2,1); }
@media (prefers-reduced-motion: reduce) { .prc-style-bar { transition: none; } }
@media (prefers-reduced-motion: reduce) {
  .prc-card *, .prc-card, .prc-motion, .prc-motion * { animation: none !important; }
}
`;

const STYLE_ID = 'prc-styles';
const FONTS_PREFIX = 'prc-font-';

/** Put the card's keyframes in <head> once. Safe to call from every card. */
export function ensureCanvasStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = PORTAL_CANVAS_CSS;
  document.head.appendChild(el);
}

/** One Google family, loaded once, only when something on screen wears it. */
export function ensureGoogleFamily(family) {
  if (typeof document === 'undefined' || !family) return;
  const id = FONTS_PREFIX + family.replace(/[^a-z0-9]/gi, '');
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  // Fetched with CORS so its cssRules can be read: that is how the share card embeds this face
  // (and only this face) into the picture it hands Instagram. Without it the rules are opaque,
  // html-to-image falls back to re-fetching the CSS, and on a phone that is where the card
  // loses its font and comes back in whatever cursive the system had lying around.
  link.crossOrigin = 'anonymous';
  link.href = `https://fonts.googleapis.com/css2?family=${family}&display=swap`;
  document.head.appendChild(link);
}

/** Every display face at once — the composer, where any of them is one tap away. */
export function ensureAllDisplayFonts() {
  FONTS.forEach((f) => { if (f.google) ensureGoogleFamily(f.google); });
}

export function usePortalCanvasStyles() {
  useEffect(() => { ensureCanvasStyles(); }, []);
}

/**
 * `all` for the composer; otherwise only what this card's layout needs (its font, and Bebas
 * Neue for a POSTER event or venue).
 */
export function useDisplayFonts(layout, { all = false, hasEvent = false, hasVenue = false } = {}) {
  const wanted = all ? 'all' : googleFamiliesFor(layout, { hasEvent, hasVenue }).join('|');
  useEffect(() => {
    if (wanted === 'all') ensureAllDisplayFonts();
    else if (wanted) wanted.split('|').forEach(ensureGoogleFamily);
  }, [wanted]);
}
